import './Claims.scss';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import { Popover } from '@lmig/lmds-react-popover';
import { IconButton, IconHelp } from '@lmig/lmds-react-icons';
import { JSX, useEffect, useState } from 'react';
import { useLocalTabDataHandler, APIList } from '../../../Data/useLocalTabDataHandler';
import { IClaimsDataJson } from '../../../Models/IClaimsDataJson';
import { Radio, RadioGroup } from '@lmig/lmds-react-radio';
import { Form } from '@lmig/lmds-react-form';
import { Link } from '@lmig/lmds-react-link';
import { Spinner } from '../../Spinner';
import { getRecentActivitiesLink } from 'Foundation/Configurations/GlobalConfiguration';

enum ClaimType {
  NewClaim = 'New Claim',
  ClaimPayment = 'Claim Payment',
  TotalLossPayment = 'Total Loss Payment',
  SubrogationUpdates = 'Subrogation Update',
  LargeLossReserveUpdates = 'Large Loss Reserve Update',
}

type ClaimRecentActivitiesLinks = {
  [key in ClaimType]: string;
};

const claimRecentActivitiesLinks: ClaimRecentActivitiesLinks = {
  [ClaimType.NewClaim]: '/?activityType=claims&transaction=new%20claim&viewing=3',
  [ClaimType.ClaimPayment]: '/?activityType=claims&transaction=claim%20payment&viewing=3',
  [ClaimType.TotalLossPayment]: '/?activityType=claims&transaction=total%20loss%20payment&viewing=3',
  [ClaimType.SubrogationUpdates]: '/?activityType=claims&transaction=subrogation%20update&viewing=3',
  [ClaimType.LargeLossReserveUpdates]: '/?activityType=claims&transaction=large%20loss%20reserve%20update&viewing=3',
};

interface ClaimTotals {
  newClaim: number;
  claimPayment: number;
  totalLossPayment: number;
  subrogationUpdates: number;
  largeLossReserveUpdates: number;
}

export default function Claims(props: { activeTab: number; children?: JSX.Element }) {
  const recentActivitiesLink = getRecentActivitiesLink();
  const { data, error, isLoading } = useLocalTabDataHandler({ api: APIList.claims });
  const [claimTotals, setClaimTotals] = useState<ClaimTotals>({
    newClaim: 0,
    claimPayment: 0,
    totalLossPayment: 0,
    subrogationUpdates: 0,
    largeLossReserveUpdates: 0,
  });
  const [allClaimsTotals, setAllClaimTotals] = useState<ClaimTotals>({
    newClaim: 0,
    claimPayment: 0,
    totalLossPayment: 0,
    subrogationUpdates: 0,
    largeLossReserveUpdates: 0,
  });

  const [catClaimTotals, setCatClaimTotals] = useState<ClaimTotals>({
    newClaim: 0,
    claimPayment: 0,
    totalLossPayment: 0,
    subrogationUpdates: 0,
    largeLossReserveUpdates: 0,
  });
  const [nonCatClaimTotals, setNonCatClaimTotals] = useState<ClaimTotals>({
    newClaim: 0,
    claimPayment: 0,
    totalLossPayment: 0,
    subrogationUpdates: 0,
    largeLossReserveUpdates: 0,
  });

  const [claimFilter, setClaimFilter] = useState<string>('all');
  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });

  useEffect(() => {
    var claimReset: ClaimTotals = {
      newClaim: 0,
      claimPayment: 0,
      totalLossPayment: 0,
      subrogationUpdates: 0,
      largeLossReserveUpdates: 0,
    };

    setClaimTotals(claimReset);

    if (claimFilter === 'cat') {
      setClaimTotals(catClaimTotals);
    }
    if (claimFilter === 'noncat') {
      setClaimTotals(nonCatClaimTotals);
    }
    if (claimFilter == 'all') {
      setClaimTotals(allClaimsTotals);
    }
  }, [claimFilter, claimTotals]);

  useEffect(() => {
    const setTotalsHelper = (claimsData: IClaimsDataJson[]) => {
      var filteredResult: ClaimTotals = {
        newClaim: claimsData.filter((c) => c.activityType.toLowerCase() === ClaimType.NewClaim.toLowerCase())?.length,
        claimPayment: claimsData.filter((c) => c.activityType.toLowerCase() === ClaimType.ClaimPayment.toLowerCase())
          ?.length,
        totalLossPayment: claimsData.filter(
          (c) => c.activityType.toLowerCase() === ClaimType.TotalLossPayment.toLowerCase(),
        )?.length,
        subrogationUpdates: claimsData.filter(
          (c) => c.activityType.toLowerCase() === ClaimType.SubrogationUpdates.toLowerCase(),
        )?.length,
        largeLossReserveUpdates: claimsData.filter(
          (c) => c.activityType.toLowerCase() === ClaimType.LargeLossReserveUpdates.toLowerCase(),
        )?.length,
      };
      return filteredResult;
    };

    const getClaimTypeCounts = (claimsData: IClaimsDataJson[]) => {
      setClaimFilter('all');
      setClaimTotals(setTotalsHelper(claimsData));
      setAllClaimTotals(setTotalsHelper(claimsData));
      setCatClaimTotals(setTotalsHelper(claimsData.filter((c) => c.catastropheLossIndicator.toUpperCase() === 'Y')));
      setNonCatClaimTotals(setTotalsHelper(claimsData.filter((c) => c.catastropheLossIndicator.toUpperCase() === 'N')));
    };

    const isValidClaimsJSON = (claim: IClaimsDataJson) => claim && claim.activityType;
    const handlePolicyData = () =>
      new Promise((resolve, reject) => {
        if (isLoading) resolve('loading');
        if (error) reject();
        if (!data?.length) reject();

        const json = data;
        let filteredJson = json.filter(isValidClaimsJSON);

        getClaimTypeCounts(filteredJson);
        resolve(filteredJson);
      });

    handlePolicyData().catch(() => {
      GetNoDataTile('claims', setNoDataTile);
    });
  }, [data, error, isLoading]);

  return isLoading ? (
    <Spinner />
  ) : data?.length > 0 ? (
    <>
      {recentActivitiesLink && recentActivitiesLink.length > 0 ? (
        <div className="claims-type-label view-all-claims">
          <Link variant="standalone" href={recentActivitiesLink + '/?activityType=claims'}>
            View all claims
          </Link>
        </div>
      ) : null}
      <div
        data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
        key={props.activeTab}
        className="claims-tab-content tab-content"
      >
        <div className="claims-tab-column">
          <div className="claims-labels-column">
            <div className="claims-labels-column-item">
              <Link
                target="_blank"
                href={
                  recentActivitiesLink
                    ? recentActivitiesLink + claimRecentActivitiesLinks[ClaimType.NewClaim]
                    : undefined
                }
              >
                <div className="claims-type-label">New claims</div>
              </Link>
              <div className="claims-totals-column">
                <div className="claims-total-text">{claimTotals.newClaim}</div>
              </div>
            </div>
            <div className="claims-labels-column-item">
              <Link
                target="_blank"
                href={
                  recentActivitiesLink
                    ? recentActivitiesLink + claimRecentActivitiesLinks[ClaimType.ClaimPayment]
                    : undefined
                }
              >
                <div className="claims-type-label">Claim Payments</div>
              </Link>
              <div className="claims-totals-column">
                <div className="claims-total-text">{claimTotals.claimPayment}</div>
              </div>
            </div>
            <div className="claims-labels-column-item">
              <Link
                target="_blank"
                href={
                  recentActivitiesLink
                    ? recentActivitiesLink + claimRecentActivitiesLinks[ClaimType.TotalLossPayment]
                    : undefined
                }
              >
                <div className="claims-type-label">Total loss payments</div>
              </Link>
              <div className="claims-totals-column">
                <div className="claims-total-text">{claimTotals.totalLossPayment}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="claims-tab-column">
          <div className="claims-labels-column two-item-column">
            <div className="claims-labels-column-item">
              <Link
                target="_blank"
                href={
                  recentActivitiesLink
                    ? recentActivitiesLink + claimRecentActivitiesLinks[ClaimType.SubrogationUpdates]
                    : undefined
                }
              >
                <div className="claims-type-label">Subrogation updates</div>
              </Link>
              <div className="claims-totals-column">
                <div className="claims-total-text">{claimTotals.subrogationUpdates}</div>
              </div>
            </div>
            <div className="claims-labels-column-item">
              <Link
                target="_blank"
                href={
                  recentActivitiesLink
                    ? recentActivitiesLink + claimRecentActivitiesLinks[ClaimType.LargeLossReserveUpdates]
                    : undefined
                }
              >
                <div className="claims-type-label claims-large-loss">
                  <div>Large-loss reserve updates</div>
                  <Popover
                    className="large-loss-reserve-content"
                    trigger={
                      <IconButton background="transparent">
                        <IconHelp color="gray" size="16" />
                      </IconButton>
                    }
                  >
                    New reserve or reserve increase/decrease of $50k or more.
                  </Popover>
                </div>
              </Link>
              <div className="claims-totals-column">
                <div className="claims-total-text">{claimTotals.largeLossReserveUpdates}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
        className="claims-radiogroup-wrapper"
      >
        <Form id="cat-noncat-input" className="claims-radiogroup-container">
          <RadioGroup name="RadioGroup--default" onChange={(e) => setClaimFilter(e.target.value)}>
            <Radio
              id="recentactivities-all"
              checked={claimFilter === 'all' ? true : false}
              labelVisual="All"
              value="all"
            />
            <Radio
              id="recentactivities-cat"
              checked={claimFilter === 'cat' ? true : false}
              labelVisual="Catastrophe"
              value="cat"
            />
            <Radio
              id="recentactivities-noncat"
              checked={claimFilter === 'noncat' ? true : false}
              labelVisual="Non catastrophe"
              value="noncat"
            />
          </RadioGroup>
        </Form>
      </div>
      <div className="claims recent-activities-footer">{props.children ?? <></>}</div>
    </>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile.leftText}
      messageRight={noDataTile.rightText}
    ></RecentActivitiesNoData>
  );
}
